// PASS 2 (B3) — the refund register. A refund raised on a cancelled or rejected order waits for
// an Admin's approval; only then can Finance mark it paid with the UTR of the transfer.
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, istToday } from "@/lib/dateFormat";

const money = (n) => "₹" + new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
const STATUS_STYLE = {
  "Pending Approval": "bg-amber-100 text-amber-900",
  "Approved": "bg-blue-100 text-blue-900",
  "Paid": "bg-green-100 text-green-900",
  "Rejected": "bg-red-100 text-red-900",
};
const TABS = ["Pending Approval", "Approved", "Paid", "Rejected", "All"];

export default function RefundsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Pending Approval");
  const [data, setData] = useState({ refunds: [], cards: {}, can_approve: false, can_pay: false });
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState("");
  const [payForm, setPayForm] = useState({ open: false, refund: null, utr: "", mode: "", paid_date: istToday() });
  const [rejectForm, setRejectForm] = useState({ open: false, refund: null, reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/refunds", { params: tab === "All" ? {} : { status: tab } });
      setData(d); setBlocked("");
    } catch (e) {
      if ([401, 403].includes(e.response?.status)) setBlocked(e.response?.data?.detail || "No access");
      else toast.error(formatApiError(e.response?.data?.detail) || "Could not load refunds");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/refunds/${r._id}/approve`, {});
      toast.success(resp.message); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (rejectForm.reason.trim().length < 5) { toast.error("Give the reason for refusing this refund"); return; }
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/refunds/${rejectForm.refund._id}/reject`, { reason: rejectForm.reason.trim() });
      toast.success(resp.message);
      setRejectForm({ open: false, refund: null, reason: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doPay = async () => {
    if (!payForm.utr.trim()) { toast.error("Enter the UTR of the refund transfer"); return; }
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/refunds/${payForm.refund._id}/mark-paid`,
        { utr: payForm.utr.trim(), mode: payForm.mode, paid_date: payForm.paid_date });
      toast.success(resp.message);
      setPayForm({ open: false, refund: null, utr: "", mode: "", paid_date: istToday() }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (blocked) return <div className="p-6 text-sm text-red-700" data-testid="refunds-blocked">{blocked}</div>;

  const cards = data.cards || {};

  return (
    <div data-testid="refunds-page">
      <div className="mb-4">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Refunds</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Money sent back to a customer after an order was cancelled or rejected. An Admin approves,
          Finance pays with a UTR.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="refund-cards">
        {[["Pending Approval", "pending-approval"], ["Approved", "approved"], ["Paid", "paid"],
          ["Rejected", "rejected"], ["Refused — money not settled", "refused-unsettled"]].map(([s, slug]) => (
          <div key={s} className={`border p-3 rounded-sm ${slug === "refused-unsettled" ? "border-red-300 bg-red-50" : "border-zinc-200"}`} data-testid={`refund-card-${slug}`}>
            <p className="text-[10px] text-zinc-400 uppercase">{s}</p>
            <p className="text-lg font-mono font-bold">{money((cards[s] || {}).amount)}</p>
            <p className="text-[10px] text-zinc-400">{(cards[s] || {}).count || 0} {slug === "refused-unsettled" ? "order(s)" : "refund(s)"}</p>
          </div>
        ))}
      </div>

      {(data.refused_unsettled || []).length > 0 && (
        <div className="mb-4 border border-red-300 bg-red-50 rounded-sm p-3" data-testid="refunds-refused-unsettled">
          <p className="text-xs font-semibold text-red-900 mb-1">Refused — money not settled. These orders hold their money again; settle them from the order page.</p>
          {(data.refused_unsettled || []).map(r => (
            <div key={r.order_id} className="text-[11px] text-red-900 flex flex-wrap gap-2 items-center py-0.5" data-testid={`refused-order-${r.order_number}`}>
              <button className="font-mono text-blue-700 underline" onClick={() => window.open(`/orders/${r.order_id}`, "_blank")}>{r.order_number}</button>
              <span>{r.customer_name}</span>
              <span className="font-mono font-semibold">{money(r.amount)}</span>
              <span>— refused by {r.refused_by} on {fmtDate((r.refused_at || "").slice(0, 10))}: {r.reason}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 mb-3">
        {TABS.map(t => (
          <Button key={t} variant={tab === t ? "default" : "outline"}
            className={`h-8 text-[11px] px-2.5 rounded-sm ${tab === t ? "bg-zinc-900 text-white" : ""}`}
            onClick={() => setTab(t)} data-testid={`refund-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</Button>
        ))}
      </div>

      <div className="border border-zinc-200 overflow-x-auto" data-testid="refunds-table">
        <table className="data-table w-full">
          <thead><tr>
            <th>Order</th><th>Customer</th><th className="num">Amount</th><th>Reason</th>
            <th>Requested</th><th>Approved by</th><th>UTR</th><th>Status</th><th>Action</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">Loading…</td></tr> :
              (data.refunds || []).length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs" data-testid="refunds-empty">No refunds here</td></tr> :
                (data.refunds || []).map(r => (
                  <tr key={r._id} data-testid={`refund-row-${r.order_number}`}>
                    <td className="font-mono text-xs">
                      <button className="text-blue-600 hover:underline" onClick={() => window.open(`/orders/${r.order_id}`, "_blank")}>{r.order_number}</button>
                    </td>
                    <td className="text-xs">{r.customer_name}</td>
                    <td className="num font-mono text-xs font-semibold">{money(r.amount)}</td>
                    <td className="text-[11px] text-zinc-600 max-w-[260px]">{r.reason}{r.rejection_reason ? <span className="block text-red-700">Refused: {r.rejection_reason}</span> : null}</td>
                    <td className="text-[11px]">{fmtDate(r.request_date)}<span className="block text-[10px] text-zinc-400">{r.requested_by_name}</span></td>
                    <td className="text-[11px]">{r.approved_by_name || "—"}</td>
                    <td className="font-mono text-[11px]">{r.utr || "—"}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${STATUS_STYLE[r.status] || ""}`} data-testid={`refund-status-${r.order_number}`}>{r.status}</span></td>
                    <td className="whitespace-nowrap">
                      {r.status === "Pending Approval" && data.can_approve && (
                        <>
                          <Button size="sm" className="h-7 text-[10px] rounded-sm mr-1" disabled={busy}
                            onClick={() => approve(r)} data-testid={`refund-approve-${r.order_number}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm text-red-700" disabled={busy}
                            onClick={() => setRejectForm({ open: true, refund: r, reason: "" })} data-testid={`refund-reject-${r.order_number}`}>Refuse</Button>
                        </>
                      )}
                      {r.status === "Pending Approval" && !data.can_approve && <span className="text-[10px] text-zinc-400">Waiting for an Admin</span>}
                      {r.status === "Approved" && data.can_pay && (
                        <Button size="sm" className="h-7 text-[10px] rounded-sm" disabled={busy}
                          onClick={() => setPayForm({ open: true, refund: r, utr: "", mode: r.mode || "", paid_date: istToday() })}
                          data-testid={`refund-pay-${r.order_number}`}>Mark paid</Button>
                      )}
                      {r.status === "Paid" && <span className="text-[10px] text-zinc-500">{fmtDate(r.paid_date)} · {r.paid_by_name}</span>}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <Dialog open={payForm.open} onOpenChange={v => setPayForm(f => ({ ...f, open: v }))}>
        <DialogContent className="rounded-sm" data-testid="refund-pay-dialog">
          <DialogHeader><DialogTitle className="text-base">Mark refund paid — {payForm.refund?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-600">{money(payForm.refund?.amount)} to {payForm.refund?.customer_name}. Approved by {payForm.refund?.approved_by_name}.</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px]">Mode</Label>
                <Input value={payForm.mode} onChange={e => setPayForm(f => ({ ...f, mode: e.target.value }))}
                  placeholder="NEFT / RTGS / UPI / Cheque" className="mt-1 h-8 text-xs rounded-sm" data-testid="refund-pay-mode" /></div>
              <div><Label className="text-[11px]">Paid on</Label>
                <Input type="date" value={payForm.paid_date} onChange={e => setPayForm(f => ({ ...f, paid_date: e.target.value }))}
                  className="mt-1 h-8 text-xs rounded-sm" data-testid="refund-pay-date" /></div>
            </div>
            <div><Label className="text-[11px]">UTR *</Label>
              <Input value={payForm.utr} onChange={e => setPayForm(f => ({ ...f, utr: e.target.value }))}
                placeholder="The bank reference of the transfer" className="mt-1 h-8 text-xs rounded-sm" data-testid="refund-pay-utr" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-8 text-xs rounded-sm" onClick={() => setPayForm(f => ({ ...f, open: false }))}>Close</Button>
            <Button className="h-8 text-xs rounded-sm" disabled={busy} onClick={doPay} data-testid="refund-pay-confirm">{busy ? "Saving…" : "Mark paid"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectForm.open} onOpenChange={v => setRejectForm(f => ({ ...f, open: v }))}>
        <DialogContent className="rounded-sm" data-testid="refund-reject-dialog">
          <DialogHeader><DialogTitle className="text-base">Refuse this refund — {rejectForm.refund?.order_number}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-[11px]">Reason *</Label>
            <Textarea rows={3} value={rejectForm.reason} onChange={e => setRejectForm(f => ({ ...f, reason: e.target.value }))}
              className="mt-1 text-xs rounded-sm" placeholder="Why is this refund not being paid?" data-testid="refund-reject-reason" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-8 text-xs rounded-sm" onClick={() => setRejectForm({ open: false, refund: null, reason: "" })}>Close</Button>
            <Button className="h-8 text-xs rounded-sm bg-red-600 hover:bg-red-700" disabled={busy} onClick={doReject} data-testid="refund-reject-confirm">{busy ? "Saving…" : "Refuse"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="mt-3 text-[10px] text-zinc-400">
        Signed in as {user?.name}. {data.can_approve ? "You can approve refunds." : "You cannot approve refunds."}
        {" "}{data.can_pay ? "You can mark an approved refund paid." : "You cannot mark a refund paid."}
      </p>
    </div>
  );
}
